import React, { useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import ReactFlow, {
  Node,
  Edge,
  Position,
  Handle,
  NodeProps,
  MarkerType,
  ReactFlowProvider,
} from "reactflow";
import "reactflow/dist/style.css";
import { X } from "lucide-react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import type { TimelineItem } from "@/models/legislation/Timeline";
import type { ZoomReason } from "./DateNavigator";
import { cleanTitle, findAncestorPath } from "./compactTimelineUtils";
import { formatDanishDate, parseSignatureDate } from "@/lib/timelineDates";
import { flattenTimeline } from "@/lib/diffViewHtml";

type ZoomedBranchViewProps = {
  root: TimelineItem;
  currentAccessionNumber: string;
  targetDate: Date;
  candidates: TimelineItem[];
  reason: ZoomReason;
  onSelect: (accessionNumber: string) => void;
  onClose: () => void;
  /** When true, nodes after the current document are visually disabled */
  compareMode?: boolean;
  /**
   * When provided, only these accessions are selectable compare targets (in
   * either direction). When null/undefined, falls back to "earlier only".
   */
  eligibleCompareAccessions?: Set<string> | null;
};

// Custom node data type
type BranchNodeData = {
  item: TimelineItem;
  isCandidate: boolean;
  isOnCurrentPath: boolean;
  isCurrent: boolean;
  isDisabled: boolean;
  onClick: (accessionNumber: string) => void;
};

// Custom hover card for branch nodes - shows only full title
const BranchNodeHoverCard: React.FC<{
  fullTitle: string;
  shortTitle: string;
  dateStr: string;
  isCurrent: boolean;
  x: number;
  y: number;
}> = ({ fullTitle, x, y }) => {
  const estimatedHeight = 80;
  const showBelow = y - estimatedHeight < 10;
  const nodeBottomY = y + 80; // Account for node height

  const style: React.CSSProperties = {
    position: "fixed",
    left: `${x}px`,
    top: showBelow ? `${nodeBottomY + 8}px` : `${y - 8}px`,
    transform: showBelow ? "translate(-50%, 0)" : "translate(-50%, -100%)",
    zIndex: 9999,
  };

  const content = (
    <div
      style={style}
      className="bg-primary text-primary-foreground rounded-lg shadow-lg px-4 py-3 max-w-md pointer-events-none"
    >
      {showBelow && (
        <div
          className="absolute left-1/2 -translate-x-1/2 -top-2 w-0 h-0"
          style={{
            borderLeft: "8px solid transparent",
            borderRight: "8px solid transparent",
            borderBottom: "8px solid hsl(var(--primary))",
          }}
        />
      )}

      {/* Full Title only */}
      <div className="text-sm whitespace-pre-line leading-snug">
        {fullTitle}
      </div>

      {!showBelow && (
        <div
          className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-0 h-0"
          style={{
            borderLeft: "8px solid transparent",
            borderRight: "8px solid transparent",
            borderTop: "8px solid hsl(var(--primary))",
          }}
        />
      )}
    </div>
  );

  return createPortal(content, document.body);
};

// Custom node component for branch view - horizontal layout
const BranchNode: React.FC<NodeProps<BranchNodeData>> = ({ data }) => {
  const { item, isCandidate, isOnCurrentPath, isCurrent, isDisabled, onClick } = data;
  const date = parseSignatureDate(item.signatureDate);
  const dateStr = date ? formatDanishDate(date) : item.signatureDate;
  const fullTitle = cleanTitle(item.title || item.shortName, true);
  const shortTitle = cleanTitle(item.shortName || item.title, true);

  // Use short title for display, truncate if too long
  const displayTitle =
    shortTitle.length > 35 ? shortTitle.substring(0, 32) + "..." : shortTitle;

  // State for custom hover tooltip
  const [hoveredPos, setHoveredPos] = useState<{ x: number; y: number } | null>(
    null
  );

  const isOutOfOrder = item.isOutOfOrder;

  return (
    <div
      className={`
        px-3 py-2 rounded-lg border-2 transition-all duration-200
        min-w-[160px] max-w-[200px]
        ${isDisabled ? "cursor-not-allowed opacity-35 select-none" : "cursor-pointer"}
        ${isOutOfOrder ? "border-dashed" : ""}
        ${
          isDisabled
            ? "border-muted-foreground/20 bg-muted/20"
            : isCandidate
              ? "border-primary bg-primary/10 ring-2 ring-primary/30 hover:bg-primary/20"
              : isOnCurrentPath
                ? "border-primary/50 bg-primary/5"
                : isOutOfOrder
                  ? "border-muted-foreground/40 bg-muted/30 hover:bg-muted/50"
                  : "border-muted-foreground/30 bg-background hover:bg-muted/50"
        }
        ${isCurrent ? "ring-2 ring-primary" : ""}
      `}
      onMouseEnter={(e) => {
        if (isDisabled) return;
        const rect = e.currentTarget.getBoundingClientRect();
        setHoveredPos({
          x: rect.left + rect.width / 2,
          y: rect.top,
        });
      }}
      onMouseLeave={() => {
        setHoveredPos(null);
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (!isDisabled && item.accessionNumber) {
          onClick(item.accessionNumber);
        }
      }}
      role={isDisabled ? undefined : "button"}
      tabIndex={isDisabled ? -1 : 0}
      aria-disabled={isDisabled || undefined}
      onKeyDown={(e) => {
        if (!isDisabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          if (item.accessionNumber) {
            onClick(item.accessionNumber);
          }
        }
      }}
    >
      {/* Handles for edges - using inline styles for React Flow compatibility */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: "transparent",
          border: "none",
          width: 8,
          height: 8,
        }}
        isConnectable={false}
      />
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span
            className={`
              text-xs font-medium
              ${isCandidate ? "text-primary" : "text-muted-foreground"}
            `}
          >
            {dateStr}
          </span>
          {isCurrent && (
            <span className="text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded">
              Nu
            </span>
          )}
          {isCandidate && !isCurrent && (
            <span className="text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded">
              Match
            </span>
          )}
          {isOutOfOrder && (
            <span className="text-xs bg-muted-foreground/20 text-muted-foreground px-1.5 py-0.5 rounded">
              Ikke i rækkefølge
            </span>
          )}
        </div>
        <div className="text-sm text-foreground line-clamp-2">
          {displayTitle}
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: "transparent",
          border: "none",
          width: 8,
          height: 8,
        }}
        isConnectable={false}
      />
      {hoveredPos && (
        <BranchNodeHoverCard
          fullTitle={fullTitle}
          shortTitle={shortTitle}
          dateStr={dateStr}
          isCurrent={isCurrent}
          x={hoveredPos.x}
          y={hoveredPos.y}
        />
      )}
    </div>
  );
};

const nodeTypes = { branchNode: BranchNode };

// Helper to build HORIZONTAL tree layout (left-to-right)
type LayoutNode = {
  item: TimelineItem;
  x: number;
  y: number;
};

const buildHorizontalTreeLayout = (
  root: TimelineItem
): { nodes: LayoutNode[]; edges: { source: string; target: string }[] } => {
  const nodes: LayoutNode[] = [];
  const edges: { source: string; target: string }[] = [];

  const nodeWidth = 200;
  const nodeHeight = 70;
  const horizontalGap = 60; // Gap between parent and children (left-right)
  const verticalGap = 30; // Gap between siblings (top-bottom)

  // Calculate subtree height (for vertical distribution)
  const getSubtreeHeight = (node: TimelineItem): number => {
    if (!node.children || node.children.length === 0) {
      return nodeHeight;
    }
    const childrenHeight = node.children.reduce(
      (sum, child) => sum + getSubtreeHeight(child) + verticalGap,
      -verticalGap
    );
    return Math.max(nodeHeight, childrenHeight);
  };

  // Position nodes horizontally (x = depth, y = vertical spread)
  const positionNode = (
    node: TimelineItem,
    x: number,
    y: number,
    parentId?: string
  ) => {
    const nodeId = node.accessionNumber || `node-${x}-${y}`;

    nodes.push({
      item: node,
      x,
      y,
    });

    if (parentId) {
      edges.push({ source: parentId, target: nodeId });
    }

    if (node.children && node.children.length > 0) {
      const subtreeHeight = getSubtreeHeight(node);
      let currentY = y - subtreeHeight / 2 + nodeHeight / 2;

      for (const child of node.children) {
        const childHeight = getSubtreeHeight(child);
        const childY = currentY + childHeight / 2 - nodeHeight / 2;
        positionNode(child, x + nodeWidth + horizontalGap, childY, nodeId);
        currentY += childHeight + verticalGap;
      }
    }
  };

  const totalHeight = getSubtreeHeight(root);
  positionNode(root, 0, totalHeight / 2);

  return { nodes, edges };
};

const ZoomedBranchViewContent: React.FC<ZoomedBranchViewProps> = ({
  root,
  currentAccessionNumber,
  targetDate,
  candidates,
  reason,
  onSelect,
  onClose,
  compareMode = false,
  eligibleCompareAccessions = null,
}) => {
  // Get current path for highlighting
  const currentPath = useMemo(() => {
    const path = findAncestorPath(root, currentAccessionNumber);
    return new Set(path.map((item) => item.accessionNumber || ""));
  }, [root, currentAccessionNumber]);

  // Get candidate accession numbers
  const candidateSet = useMemo(
    () => new Set(candidates.map((c) => c.accessionNumber || "")),
    [candidates]
  );

  // Compute which accession numbers come after the current document in the
  // flat chronological order — these should be disabled in compare mode.
  const afterCurrentSet = useMemo(() => {
    if (!compareMode) return new Set<string>();
    const flat = flattenTimeline([root]);
    const currentIdx = flat.findIndex(
      (n) => n.accessionNumber === currentAccessionNumber
    );
    if (currentIdx === -1) return new Set<string>();
    return new Set(
      flat
        .slice(currentIdx) // currentIdx itself is also disabled (can't compare with self)
        .map((n) => n.accessionNumber || "")
        .filter(Boolean)
    );
  }, [compareMode, root, currentAccessionNumber]);

  // With an explicit eligibility set, anything outside the LBK window (or the
  // current doc itself) is disabled, regardless of direction. Otherwise keep
  // the legacy "after current" rule.
  const isDisabledInCompare = useCallback(
    (accession: string): boolean => {
      if (!compareMode) return false;
      if (eligibleCompareAccessions) {
        return (
          accession === currentAccessionNumber ||
          !eligibleCompareAccessions.has(accession)
        );
      }
      return afterCurrentSet.has(accession);
    },
    [afterCurrentSet, compareMode, currentAccessionNumber, eligibleCompareAccessions]
  );

  // Build horizontal layout
  const { layoutNodes, layoutEdges } = useMemo(() => {
    const layout = buildHorizontalTreeLayout(root);
    return { layoutNodes: layout.nodes, layoutEdges: layout.edges };
  }, [root]);

  // Convert to React Flow format
  const { nodes, edges } = useMemo(() => {
    // Compute the actual color value from CSS variable for SVG compatibility
    const mutedForegroundHsl = getComputedStyle(document.documentElement)
      .getPropertyValue("--muted-foreground")
      .trim();
    const edgeColor = mutedForegroundHsl
      ? `hsl(${mutedForegroundHsl} / 0.4)`
      : "#9ca3af"; // fallback to gray-400

    const flowNodes: Node<BranchNodeData>[] = layoutNodes.map((ln) => ({
      id: ln.item.accessionNumber || `node-${ln.x}-${ln.y}`,
      type: "branchNode",
      position: { x: ln.x, y: ln.y },
      data: {
        item: ln.item,
        isCandidate: candidateSet.has(ln.item.accessionNumber || ""),
        isOnCurrentPath: currentPath.has(ln.item.accessionNumber || ""),
        isCurrent: ln.item.accessionNumber === currentAccessionNumber,
        isDisabled: isDisabledInCompare(ln.item.accessionNumber || ""),
        onClick: onSelect,
      },
      draggable: false,
    }));

    const flowEdges: Edge[] = layoutEdges.map((le, i) => ({
      id: `edge-${i}`,
      source: le.source,
      target: le.target,
      type: "smoothstep",
      style: {
        stroke: edgeColor,
        strokeWidth: 2,
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: edgeColor,
      },
    }));

    return { nodes: flowNodes, edges: flowEdges };
  }, [
    layoutNodes,
    layoutEdges,
    candidateSet,
    currentPath,
    currentAccessionNumber,
    isDisabledInCompare,
    onSelect,
  ]);

  // Get appropriate message based on reason
  const getMessage = () => {
    if (reason === "multiple_docs") {
      return `Der er flere versioner for datoen ${formatDanishDate(targetDate)}. Vælg hvilken du vil navigere til.`;
    }
    return "Navigering krydser en forgrening. Vælg hvilken gren du vil følge.";
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm">
      <div className="absolute inset-4 bg-background rounded-lg border shadow-lg flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="font-semibold">Vælg version</h3>
            <p className="text-sm text-muted-foreground">{getMessage()}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 px-4 py-2 border-b text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border-2 border-primary bg-primary/10" />
            <span>Matchende dato</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border-2 border-primary/50 bg-primary/5" />
            <span>Nuværende gren</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border-2 border-muted-foreground/30 bg-background" />
            <span>Andre versioner</span>
          </div>
        </div>

        {/* React Flow container */}
        <div className="flex-1 min-h-0">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            panOnScroll={false}
            zoomOnScroll
            zoomOnPinch
            minZoom={0.2}
            maxZoom={2}
            proOptions={{ hideAttribution: true }}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            defaultEdgeOptions={{
              type: "smoothstep",
              animated: false,
            }}
            onNodeClick={() => {
              // Empty handler required for React Flow event delegation
            }}
            onNodeMouseEnter={() => {
              // Empty handler required for React Flow event delegation
            }}
          />
        </div>

        {/* Footer */}
        <div className="p-4 border-t text-sm text-muted-foreground">
          Klik på en boks for at vælge den som dit nye udgangspunkt. Brug musen
          til at panorere og zoome.
        </div>
      </div>
    </div>
  );
};

// Wrap in ReactFlowProvider
const ZoomedBranchView: React.FC<ZoomedBranchViewProps> = (props) => {
  return (
    <ReactFlowProvider>
      <ZoomedBranchViewContent {...props} />
    </ReactFlowProvider>
  );
};

export default ZoomedBranchView;
